import { useTranslation } from 'react-i18next';
import { IFleetVesselObject } from '../../../types/list';
import { IGridConfig } from '../../../types/grid';
import Grid from '../../common/grid/Grid';
import DataGrid from 'devextreme-react/data-grid';
import { exportDataGrid } from 'devextreme/excel_exporter';
import saveAs from 'file-saver';
import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { Restricted, usePermission } from '../../../contexts/PermissionContext';
import Switch from 'devextreme-react/switch';
import Button from 'devextreme-react/button';
import icons from '../../common/icons/icons';
import ConfirmDialog from '../../common/confirm/ConfirmDialog';
import { IDialogConfig } from '../../../types/dialog';
import { MarsApiService as api } from '../../../api/mars-api-service';
import { useLoaderContext } from '../../../contexts/LoaderContext';
import { MarsApiConfig as apiConfig } from '../../../api/mars-api-config';
import { Workbook } from 'exceljs';

function FleetVesselListGrid(
  props: {
    fleetVesselList: IFleetVesselObject;
    setFleetVesselId: any;
  },
  ref: any
) {
  const { fleetVesselList, setFleetVesselId } = props;
  const { t } = useTranslation();
  const [dataSource, setDataSource] = useState<any>();
  const [activeConfirmDialog, setActiveConfirmDialog] = useState(false);
  const { setIsLoading } = useLoaderContext();
  const { findFeature } = usePermission();
  const [gridInstance, setGridInstance] = useState<any>();

  const confirmStatusDialogConfig: IDialogConfig = {
    id: 'confirmSave',
    handleSubmit: () => {
      setActiveConfirmDialog(false);
    },
    handleClose: () => {
      setFleetVesselId();
      setActiveConfirmDialog(false);
    },
  };

  useImperativeHandle(ref, () => ({
    exportListEntriesGrid,
  }));

  const gridRef = useRef<DataGrid>();
  const onGridInitialized = (e: any) => {
    setGridInstance(e.component);
  };

  const exportListEntriesGrid = (name: any) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');
    gridRef.current?.instance.beginUpdate();
    gridRef.current?.instance.columnOption('action', 'visible', false);
    exportDataGrid({
      component: gridInstance,
      worksheet: worksheet,
    })
      .then(function () {
        workbook.xlsx.writeBuffer().then(function (buffer) {
          saveAs(
            new Blob([buffer], { type: 'application/octet-stream' }),
            `${name}.xlsx`
          );
        });
      })
      .then(() => {
        gridRef.current?.instance.columnOption('action', 'visible', true);
        gridRef.current?.instance.endUpdate();
      });
  };

  const handleActiveInactiveChange = async (
    e: any,
    fleetVessel: any,
    isDelete: boolean
  ) => {
    if (isDelete) {
      const dialogConfig = {
        title: t('toast.confirmation'),
        content: t('toast.deleteFleetVessel'),
        handleSubmit: () => {
          deleteFleet(fleetVessel?.fleetVesselId);
          setActiveConfirmDialog(false);
        },
      };
      setActiveDialogConfig({
        ...confirmStatusDialogConfig,
        ...dialogConfig,
      });
      setActiveConfirmDialog(true);
    } else {
      const dialogConfig = {
        title: t('toast.confirmation'),
        content: e
          ? t('toast.activeFleetVesselStatus')
          : t('toast.deactiveFleetVesselStatus'),
        handleSubmit: () => {
          activateList(e, fleetVessel?.fleetVesselId);
          setActiveConfirmDialog(false);
        },
      };
      setActiveDialogConfig({
        ...confirmStatusDialogConfig,
        ...dialogConfig,
      });
      setActiveConfirmDialog(true);
    }
  };

  const activateList = async (isactive: boolean, fleetVesselId: any) => {
    const statusJson: any = {
      fleetVesselId: fleetVesselId,
      active: isactive,
    };
    api
      .put({ url: apiConfig.fleetVesselStatus, data: statusJson }, setIsLoading)
      .then(() => {
        setFleetVesselId();
      });
  };

  const deleteFleet = async (fleetVesselId: any) => {
    const statusJson: any = {
      fleetVesselId: fleetVesselId,
    };
    api
      .delete(
        { url: apiConfig.fleetVesselDelete, data: statusJson },
        setIsLoading
      )
      .then(() => {
        setFleetVesselId();
      });
  };

  const [activeDialogConfig, setActiveDialogConfig] = useState(
    confirmStatusDialogConfig
  );

  const cellRender = (data: any) => {
    return (
      <div className="m-l-field-wrap">
        <div className="m-l-toggle-switch">
          <Switch
            elementAttr={{ 'data-testid': 'statusToggleswitch' }}
            defaultValue={data.value}
            onValueChange={(e) => {
              handleActiveInactiveChange(e, data.data, false);
            }}
          />
        </div>
      </div>
    );
  };

  const actionCellTemplate = (data: any) => {
    return (
      <div className="action-cell">
        <Button
          className="m-l-btn--icon-plain"
          onClick={(e) => {
            handleActiveInactiveChange(e, data.data, true);
          }}
        >
          <icons.Trash />
        </Button>
      </div>
    );
  };

  const yesNoDataSource = [
    {
      name: t('labels.active'),
      value: true,
    },
    {
      name: t('labels.inActive'),
      value: false,
    },
  ];

  const gridConfig: IGridConfig = {
    dataSource: fleetVesselList,
    cellPrepared: cellPrepared,

    defaultColumns: findFeature('MyWatchList.ListVessels')
      ? [
          {
            caption: t('labels.vesselImo'),
            dataField: 'imoNumber',
            dataType: 'string',
            minWidth: 150,
            lookup: dataSource,
          },
          {
            caption: t('labels.vesselName'),
            dataField: 'vesselName',
            dataType: 'string',
            minWidth: 150,
          },
          {
            caption: t('labels.active'),
            dataField: 'active',
            dataType: 'string',
            minWidth: 150,
            cellTemplate: 'statusTemplate',
            allowFiltering: true,
            allowSorting: true,
            lookup: {
              dataSource: yesNoDataSource,
              valueExpr: 'value',
              displayExpr: 'name',
            },
          },
          {
            caption: t('labels.action'),
            dataField: 'action',
            dataType: 'string',
            cellTemplate: 'actionTemplate',
            alignment: 'center',
            minWidth: 100,
            allowFiltering: false,
            allowSorting: false,
            visible: findFeature('MyWatchList.DeleteVessels'),
          },
        ]
      : [],
    showHeader: false,
    showAddButton: false,
    showExport: false,
    hidePagination: !findFeature('MyWatchList.ListVessels'),
    noDataText: t('labels.addNewList'),
    ref: gridRef,
    initialized: onGridInitialized,
  };

  return (
    <div className="m-c-grid" data-testid="EditListGrid">
      <Grid
        gridConfig={gridConfig}
        statusTemplate={cellRender}
        actionTemplate={actionCellTemplate}
      />
      <ConfirmDialog
        dialogConfig={activeDialogConfig}
        isOpen={activeConfirmDialog}
      />
    </div>
  );
}

const cellPrepared = (e: any) => {
  if (e.rowType === 'data' && e.column.dataField === 'active') {
    e.cellElement.className =
      e.data.active === 'Inactive'
        ? 'status-inactive-wtout'
        : 'status-active-wtout';
  }
};

const EditFleetVesselListGrid = forwardRef(FleetVesselListGrid);
export default EditFleetVesselListGrid;
